import React, { useEffect, useRef } from "react";
import { Outlet, useLocation, useNavigationType } from "react-router-dom";
import TopNav from "./TopNav";
import LeftRail from "./LeftRail";
import { useRole } from "../context/RoleContext";
import { bumpHistoryDepth, resetHistoryDepth } from "./BackBar";
import ImpersonationBanner from "./ImpersonationBanner";
import OpenWorkBar from "./OpenWorkBar";

// Authenticated portal routes — used to persist last-visited route per role
// so a returning user lands back on the right portal page (Prompt 9.3 P2).
const AUTH_PREFIXES = ["/owner", "/architect", "/city", "/admin"];

function isAuthenticatedRoute(pathname) {
  return AUTH_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

export const LAST_ROUTE_KEY = (role) => `wn.last-route.${role}`;

export default function Shell() {
  const { pathname, search } = useLocation();
  const navType = useNavigationType(); // PUSH / POP / REPLACE
  const { isAnonymous, role } = useRole();

  // Track in-app navigation depth so <BackBar/> knows whether to call
  // navigate(-1) (real history) or fall through to its declared parent
  // route. PUSH = went forward → increment. POP = came back via the
  // browser/back-button → decrement (clamp at 0). REPLACE doesn't change
  // depth. We persist in sessionStorage so re-mounts (e.g. on auth
  // transitions) don't lose the count within a single tab session.
  const isInitialRef = useRef(true);
  useEffect(() => {
    if (isInitialRef.current) {
      isInitialRef.current = false;
      resetHistoryDepth();
      return;
    }
    if (navType === "PUSH") bumpHistoryDepth();
    else if (navType === "POP") {
      // Decrement, clamped at 0, via direct sessionStorage access.
      try {
        const n = Number(sessionStorage.getItem("wn.histDepth") || 0);
        sessionStorage.setItem(
          "wn.histDepth",
          String(Math.max(0, n - 1)),
        );
      } catch {
        /* ignore */
      }
    }
  }, [pathname, search, navType]);

  // Landing is always shown in its public marketing form, regardless of
  // any session that may exist in localStorage — TopNav with the "Sign in"
  // CTA, no LeftRail, no signed-in user chrome. From there the visitor
  // can choose any role to step into the demo.
  const isLanding = pathname === "/";
  const useRail = !isAnonymous && !isLanding;

  // Persist last visited *portal* route per role so re-entry lands them
  // back on their dashboard / saved / inbox view.
  useEffect(() => {
    if (!isAnonymous && role && isAuthenticatedRoute(pathname)) {
      try {
        localStorage.setItem(LAST_ROUTE_KEY(role), pathname + (search || ""));
      } catch {
        /* ignore quota errors */
      }
    }
  }, [pathname, search, role, isAnonymous]);

  if (useRail) {
    // Civic Systems chrome — LeftRail + content for every signed-in route.
    return (
      <div className="wn-page" data-testid="app-shell-portal" style={{ display: "flex", minHeight: "100vh", flexDirection: "column" }}>
        <ImpersonationBanner />
        <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
          <LeftRail />
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            <OpenWorkBar />
            <main
              className="animate-fade-in-up"
              style={{
                flex: 1,
                minWidth: 0,
                padding: "20px 40px 56px",
                margin: 0,
              }}
            >
              <Outlet />
            </main>
          </div>
        </div>
      </div>
    );
  }

  // Public surfaces — top nav + open editorial shell (Prompt 9.2).
  return (
    <div className="wn-page" data-testid="app-shell">
      <ImpersonationBanner />
      <TopNav />
      <main className="max-w-[1600px] mx-auto px-6 lg:px-16 xl:px-24 py-10 animate-fade-in-up">
        <Outlet />
      </main>
      {!isLanding && (
        <footer
          className="max-w-[1600px] mx-auto px-6 lg:px-16 xl:px-24 py-10"
          data-testid="app-footer"
        >
          <div
            className="border-t pt-6"
            style={{ borderColor: "#D9D8E4" }}
          >
            <p
              style={{
                color: "#6C6B85",
                fontSize: 13,
                lineHeight: 1.55,
                margin: 0,
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
              }}
              data-testid="app-footer-line"
            >
              <span
                style={{
                  fontFamily: '"Fraunces", Georgia, serif',
                  color: "#1B1F4B",
                  fontWeight: 600,
                  fontStyle: "normal",
                }}
              >
                WHOLE Neighborhoods
              </span>
              <span style={{ margin: "0 8px", opacity: 0.55 }}>—</span>
              written, block by block, with the City of Indianapolis.
            </p>
          </div>
        </footer>
      )}
    </div>
  );
}
